import * as THREE from 'three'

/**
 * 多个节点包资源作用域组成的独立所有权句柄。
 *
 * 集合创建时会 retain 每个来源 scope，因此组合结果与节点包结果可以独立释放；它只增加
 * 引用计数，不复制 Object3D、Geometry、Material 或 Texture。
 */
export class ImportedModelResourceScopeCollection implements PuppyCAD.ImportedModelResourceScopeHandle {
  /** 当前集合是否已经归还全部子作用域。 */
  private released = false

  /** 当前集合覆盖的稳定 node 身份。 */
  public readonly nodeIds: readonly string[]

  /** 当前集合尚未归还的子作用域；释放后清空以断开资源链路。 */
  private ownedScopes: PuppyCAD.ImportedModelResourceScopeHandle[]

  /**
   * 从一组来源 scope 创建独立 retained 集合。
   *
   * @param sourceScopes 节点包结果当前持有的资源作用域。
   * @returns 独立于来源 scope 生命周期的新集合。
   */
  public static retain(
    sourceScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
  ): ImportedModelResourceScopeCollection {
    return new ImportedModelResourceScopeCollection(this.retainScopes(sourceScopes, []))
  }

  /**
   * 接管调用方已经取得的独立子作用域句柄。
   *
   * @param ownedScopes 调用方不再继续释放的独立句柄。
   * @returns 统一负责后续释放的集合。
   */
  public static adopt(
    ownedScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
  ): ImportedModelResourceScopeCollection {
    return new ImportedModelResourceScopeCollection([...ownedScopes])
  }

  /**
   * 创建已经取得所有权的子作用域集合。
   *
   * @param ownedScopes 当前集合独占的子作用域句柄。
   */
  private constructor(ownedScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[]) {
    this.ownedScopes = [...ownedScopes]
    this.nodeIds = Object.freeze(ownedScopes.flatMap((scope) => scope.nodeIds))
  }

  /** 为集合内全部真实资源创建另一个独立所有权句柄。 */
  public retain(): PuppyCAD.ImportedModelResourceScopeHandle {
    this.assertAvailable()
    return ImportedModelResourceScopeCollection.retain(this.ownedScopes)
  }

  /**
   * 保留不属于目标旧子树的资源。
   *
   * @param roots 被局部替换的旧 Object3D 根。
   * @returns 每个子作用域完成过滤后的独立集合。
   */
  public retainExcludingRoots(
    roots: readonly THREE.Object3D[],
  ): PuppyCAD.ImportedModelResourceScopeHandle {
    this.assertAvailable()
    return new ImportedModelResourceScopeCollection(
      ImportedModelResourceScopeCollection.retainScopes(this.ownedScopes, roots),
    )
  }

  /** 幂等归还集合持有的全部子作用域。 */
  public release(): void {
    if (this.released) {
      return
    }
    this.released = true
    const ownedScopes = this.ownedScopes
    this.ownedScopes = []
    const errors: unknown[] = []
    for (let index = ownedScopes.length - 1; index >= 0; index -= 1) {
      try {
        ownedScopes[index]?.release()
      } catch (error) {
        errors.push(error)
      }
    }
    if (errors.length > 0) {
      throw new AggregateError(errors, '导入模型资源作用域集合释放失败')
    }
  }

  /**
   * 原子取得一组子作用域；任一 retain 失败时回滚已经取得的句柄。
   *
   * @param sourceScopes 来源作用域。
   * @param excludedRoots 需要从每个来源排除的旧子树；空数组表示完整 retain。
   * @returns 已全部取得所有权的子作用域。
   */
  private static retainScopes(
    sourceScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
    excludedRoots: readonly THREE.Object3D[],
  ): PuppyCAD.ImportedModelResourceScopeHandle[] {
    const retainedScopes: PuppyCAD.ImportedModelResourceScopeHandle[] = []
    try {
      sourceScopes.forEach((scope) => {
        retainedScopes.push(
          excludedRoots.length > 0
            ? scope.retainExcludingRoots(excludedRoots)
            : scope.retain(),
        )
      })
      return retainedScopes
    } catch (retainError) {
      const rollbackErrors: unknown[] = []
      for (let index = retainedScopes.length - 1; index >= 0; index -= 1) {
        try {
          retainedScopes[index]?.release()
        } catch (releaseError) {
          rollbackErrors.push(releaseError)
        }
      }
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [retainError, ...rollbackErrors],
          '导入模型资源作用域集合 retain 失败且回滚不完整',
        )
      }
      throw retainError
    }
  }

  /** 已释放集合禁止再次创建所有权句柄。 */
  private assertAvailable(): void {
    if (this.released) {
      throw new Error('已释放的导入模型资源作用域集合不能再次 retain')
    }
  }
}
